import { Fragment, type FC } from 'react'
import { observer } from 'mobx-react-lite'
import { Box, Error, Label, Warning } from 'shared/ui'
import { makeTextFieldAutoFocus, makeTextFieldHighlightLimit, TextField } from 'shared/ui/TextField'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { useSenderDetailsContext } from 'pages/settings/pages/rcsV2/pages/create/SenderDetails/hooks/useSenderDetailsContext'

export const BrandInfoSection: FC = observer(() => {
  const {
    brandInfoStore: {
      brandName,
      setBrandName,
      brandNameError,
      validateBrandName,
      brandDescription,
      setBrandDescription,
      brandDescriptionError,
      validateBrandDescription,
      messageType,
      setMessageType,
      messageTypeError,
      messageTypeOptions,
      messageTypesLoading,
      loadMessageTypes,
      brandNameMaxLength,
      isBrandNameMaxLengthReached,
      brandDescriptionMaxLength,
      isBrandDescriptionMaxLengthReached,
    },
  } = useSenderDetailsContext()

  return (
    <Fragment>
      <Box display={'grid'} gap={'8px'} mb={'24px'}>
        <Label
          height={16}
          label={{
            text: 'Brand name',
            textColor: 'var(--content-primary-primary)',
            horizontal: true,
          }}
        />
        <TextField
          size='medium'
          variant='stroke'
          error={brandNameError}
          InputProps={{
            placeholder: 'Enter name',
            maxLength: brandNameMaxLength,
          }}
          value={brandName}
          mainActions={[
            makeTextFieldAutoFocus({ withFocus: true }),
            makeTextFieldHighlightLimit({ limit: brandNameMaxLength }),
          ]}
          onChange={setBrandName}
          onBlur={validateBrandName}
        />
        <Error type='text' error={brandNameError} />
        <Warning
          text={isBrandNameMaxLengthReached ? `${brandNameMaxLength}-character limit reached` : ''}
        />
      </Box>
      <Box display={'grid'} gap={'8px'} mb={'24px'}>
        <Label
          height={16}
          questionTooltipProps={{
            type: 'description',
            label:
              'Describe your business and its relationship to the sender’s purpose, ensuring it matches the use case and clearly states the type of messages this sender will be used for.',
          }}
          label={{
            textColor: 'var(--content-primary-primary)',
            text: 'Brand description',
            horizontal: true,
          }}
        />
        <TextField
          size='medium'
          variant='stroke'
          error={brandDescriptionError}
          InputProps={{
            placeholder: 'Enter description',
            maxLength: brandDescriptionMaxLength,
          }}
          mainActions={[makeTextFieldHighlightLimit({ limit: brandDescriptionMaxLength })]}
          value={brandDescription}
          onChange={setBrandDescription}
          onBlur={validateBrandDescription}
        />
        <Error type='text' error={brandDescriptionError} />
        <Warning
          text={
            isBrandDescriptionMaxLengthReached
              ? `${brandDescriptionMaxLength}-character limit reached`
              : ''
          }
        />
      </Box>
      <Box mb={'40px'}>
        <SelectField
          labelGap='8px'
          labelProps={{
            height: 16,
            label: {
              text: 'What kinds of messages would you like to send?',
              textColor: 'var(--content-primary-primary)',
              horizontal: true,
            },
          }}
          placeholder='Select'
          items={messageTypeOptions}
          loading={messageTypesLoading}
          dropdownProps={{
            onToggle: (open) => {
              if (open) loadMessageTypes()
            },
          }}
          value={messageType}
          setValue={setMessageType}
          error={messageTypeError}
          size='medium'
        />
      </Box>
    </Fragment>
  )
})
